import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Search, Ban, ChevronLeft, ChevronRight, LockOpen } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { isBusiness } from '@/lib/roles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TYPES = [['all', 'Tutti'], ['user', 'Clienti'], ['business', 'Attività']];

const statusOf = (u) => {
  if (u.deleted_at || u.account_status === 'deleted') return ['Cancellato', 'bg-secondary text-muted-foreground'];
  if (u.is_active === false) return ['Bloccato', 'bg-red-50 text-red-700'];
  if (u.account_status === 'pending_contract') return ['In attesa contratto', 'bg-amber-50 text-amber-700'];
  return ['Attivo', 'bg-emerald-50 text-emerald-700'];
};

const StatusActions = ({ u, i, canGrant, onRole, onDeactivate, onActivate }) => (
  <div className="flex gap-1">
    {canGrant && <Select onValueChange={(v) => onRole(u.user_id, v)}>
      <SelectTrigger className="h-7 w-24 text-[10px] rounded" data-testid={`role-select-${i}`}><SelectValue placeholder={isBusiness(u) ? 'business' : u.role} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="user">user</SelectItem>
        <SelectItem value="business">business</SelectItem>
      </SelectContent>
    </Select>}
    {u.is_active === false
      ? <Button variant="outline" size="sm" onClick={() => onActivate(u.user_id)} className="h-7 px-2 text-[10px] rounded" data-testid={`activate-${i}`}><LockOpen className="w-3 h-3 mr-1" />Sblocca</Button>
      : <Button variant="outline" size="sm" onClick={() => window.confirm(`Bloccare ${u.email}? Verrà disconnesso da web e app.`) && onDeactivate(u.user_id)} className="h-7 px-2 text-[10px] rounded text-destructive" data-testid={`deactivate-${i}`}><Ban className="w-3 h-3 mr-1" />Blocca</Button>}
  </div>
);

const UserRow = (props) => {
  const { u, i } = props;
  const [label, cls] = statusOf(u);
  const editable = u.role !== 'system' && label !== 'Cancellato';
  return (
    <TableRow data-testid={`admin-user-${i}`}>
      <TableCell className="text-xs font-mono text-muted-foreground" title={u.user_id}>{u.user_id.slice(0, 12)}...</TableCell>
      <TableCell className="text-xs font-sans">{u.email}</TableCell>
      <TableCell className="text-xs font-sans" data-testid={`admin-user-name-${i}`}>
        {u.business_name || u.name}{u.business_name && u.business_name !== u.name && <span className="block text-muted-foreground">{u.name}</span>}
      </TableCell>
      <TableCell><Badge data-testid={`admin-user-role-${i}`} className={`text-[10px] ${isBusiness(u) ? 'bg-amber-50 text-amber-700' : 'bg-secondary text-secondary-foreground'}`}>{u.role === 'system' ? 'Account tecnico' : isBusiness(u) ? 'business' : u.role}</Badge></TableCell>
      <TableCell><Badge data-testid={`admin-user-status-${i}`} className={`text-[10px] ${cls}`}>{label}</Badge></TableCell>
      <TableCell className="text-xs font-sans font-semibold">{u.balance?.toFixed(2)}</TableCell>
      <TableCell className="text-xs font-sans">{u.review_count}</TableCell>
      <TableCell>
        {editable ? <StatusActions {...props} /> : <span className="text-[10px] text-muted-foreground font-sans" data-testid={`system-locked-${i}`}>—</span>}
      </TableCell>
    </TableRow>
  );
};

export const AdminUsersTab = ({ canGrant, onChangeRole, onDeactivate, onActivate }) => {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [type, setType] = useState('all');
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(false);

  useEffect(() => { const t = setTimeout(() => { setDebounced(q); setPage(1); }, 350); return () => clearTimeout(t); }, [q]);
  const load = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/admin/users`, { withCredentials: true, params: { q: debounced, account_type: type, page, page_size: 25 } })
      .then(r => setData(r.data)).finally(() => setLoading(false));
  }, [debounced, type, page]);
  useEffect(() => { load(); }, [load]);
  const after = (fn) => async (...args) => { await fn(...args); load(); };

  return (
    <Card className="border border-border rounded-xl"><CardContent className="p-0">
      <div className="flex flex-wrap items-center gap-3 p-4 border-b border-border">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" strokeWidth={1.5} />
          <Input value={q} onChange={e => setQ(e.target.value)} placeholder="Cerca per email, nome, ID o nome attività" className="pl-9 h-9 text-sm" data-testid="admin-users-search" />
        </div>
        <div className="flex gap-1 bg-secondary rounded-full p-1" data-testid="admin-users-type-filter">
          {TYPES.map(([v, l]) => (
            <button key={v} onClick={() => { setType(v); setPage(1); }} data-testid={`admin-users-type-${v}`}
              className={`px-3 py-1 rounded-full text-xs font-sans transition-colors ${type === v ? 'bg-background shadow-sm font-semibold' : 'text-muted-foreground'}`}>{l}</button>
          ))}
        </div>
        <span className="text-xs text-muted-foreground font-sans" data-testid="admin-users-total">{data.total} risultati</span>
      </div>
      <div className={`overflow-x-auto transition-opacity ${loading ? 'opacity-60' : ''}`}>
        <Table>
          <TableHeader><TableRow>
            {['ID', 'Email', 'Nome', 'Ruolo', 'Stato', 'Balance', 'Reviews', 'Azioni'].map(h => <TableHead key={h} className="font-sans text-xs">{h}</TableHead>)}
          </TableRow></TableHeader>
          <TableBody>
            {data.items.map((u, i) => <UserRow key={u.user_id} u={u} i={i} canGrant={canGrant} onRole={after(onChangeRole)} onDeactivate={after(onDeactivate)} onActivate={after(onActivate)} />)}
            {!loading && data.items.length === 0 && <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground py-8" data-testid="admin-users-empty">Nessun utente trovato</TableCell></TableRow>}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center justify-end gap-2 p-3 border-t border-border">
        <Button variant="outline" size="sm" className="h-8 rounded-full" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="admin-users-prev"><ChevronLeft className="w-4 h-4" /></Button>
        <span className="text-xs font-sans" data-testid="admin-users-page">Pagina {page} di {data.pages}</span>
        <Button variant="outline" size="sm" className="h-8 rounded-full" disabled={page >= data.pages} onClick={() => setPage(page + 1)} data-testid="admin-users-next"><ChevronRight className="w-4 h-4" /></Button>
      </div>
    </CardContent></Card>
  );
};
